import { TFunction, withTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import usePagebuilder from '../../../hooks/pagebuilder/usePagebuilder';
import { PageData } from '../../../interfaces/interfaces';
import { UiComponent } from '../../../interfaces/pagebuilder';
import {
  fetchAndSetBooths,
  setIsPBBoothsModal,
  setIsPBMatchingModal,
  setIsPBPresentationsModal,
  setPBSpeakersModalComponent,
  setShowBoothOnboardModal,
} from '../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../reduxToolkit/typedRedux';
import BoothOnboardModal from '../../account/event/BoothOnboardModal';
import VyDrawer from '../../common/VyDrawer';
import VyModal from '../../common/VyModal';
import PBPresentationsModal from '../templates/VystemLight/UIComponents/PresentationUiComponent/admin';
import AddModal from './Modals/AddModal';
import AddSpeakerModal from './Modals/PBAddSpeakerModal';
import PBSpeakersModal from './Modals/PBSpeakersModal';
import PBBooths from './PBBooths';
import PBMatchingModal from './PBMatchingModal';
import {
  AgendSettings,
  AllMatchesSettings,
  BoothLogoListSettings,
  BoothsSettings,
  ChallengeSettings,
  ColumnSettings,
  CountdownSettings,
  DividerSettings,
  EventHomeSettings,
  EventsListSettings,
  GallerySettings,
  HeroMenuSettings,
  HTMLSettings,
  ImageSettings,
  InfoBoothSettings,
  MarginSettings,
  MediaSettings,
  NetworkSettings,
  PhotoBoothSettings,
  PresentationPreviewListSettings,
  PresentationSettings,
  QASettings,
  QueueSettings,
  SignupFormSettings,
  SocialMediaSettings,
  SpeakersSettings,
  StageSettings,
  TeamHeroMenuSettings,
  UserProfileSettings,
  VideoSettings,
} from './Settings';
import AppointmentSettings from './Settings/appointment/AppointmentSettings';
import CTAButtonSettings from './Settings/ctabutton/CTAButtonSettings';
import PollSettings from './Settings/poll/PollSettings';
import SpeedDatingSettings from './Settings/speeddating/SpeedDatingSettings';
import TicketcodeSettings from './Settings/ticketcode/TicketcodeSettings';

interface PageBuilderInterface {
  children: any;
  isLandingPage: boolean;
  pageData: PageData | null;
  onAddComponent: number;
  onEditComponent: number;
  onDeleteComponent: number;
  onMoveComponent: {
    componentid: string;
    index: number;
  } | null;
  onMoveOutComponent: {
    componentid: string;
    pageid: string;
  } | null;
  onCopyComponent: {
    componentid: string;
    pageid: string;
  } | null;
  onCloseModals: () => void;
  onMoveFinished: (index: number) => void;
  onMoveOutFinished: () => void;
  onCopyFinished: () => void;
  t: TFunction;
}

const PageBuilder = ({
  children,
  isLandingPage = false,
  onAddComponent = -1,
  onEditComponent = -1,
  onDeleteComponent = -1,
  pageData,
  onMoveComponent = null,
  onMoveOutComponent = null,
  onCloseModals,
  onMoveFinished,
  onMoveOutFinished,
  onCopyComponent = null,
  onCopyFinished,
  t,
}: PageBuilderInterface) => {
  const [settingsElement, setSettingsElement] = useState<any>();

  const event = useSelector((state) => state.common.event);
  const isPBEditSidebar = useSelector((state) => state.common.isPBEditSidebar);
  const isPBAddModal = useSelector((state) => state.common.isPBAddModal);
  const pagebuilder = useSelector((state) => state.common.pagebuilder);
  const PBSelectedComponentIndex = useSelector((state) => state.common.PBSelectedComponentIndex);
  const PBComponentToEdit = useSelector((state) => state.common.PBComponentToEdit);
  const PBSpeakersModalComponent = useSelector((state) => state.common.PBSpeakersModalComponent);
  const isPBBoothsModal = useSelector((state) => state.common.isPBBoothsModal);
  const isPBPresentationsModal = useSelector((state) => state.common.isPBPresentationsModal);
  const PBAddSpeakerComponent = useSelector((state) => state.common.PBAddSpeakerComponent);
  const isPBMatchingModal = useSelector((state) => state.common.isPBMatchingModal);
  const showBoothOnboardModal = useSelector((state) => state.common.showBoothOnboardModal);
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);

  const {
    isUpdating,
    addComponent,
    onEditClick,
    closeModals,
    editComponent,
    onCreateComponent,
    deleteComponent,
    moveOutComponent,
    copyComponent,
    moveComponent,
    updatePageBuilder,
    cancelChanges,
  } = usePagebuilder(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (onAddComponent >= 0) addComponent(onAddComponent);
  }, [onAddComponent]);

  useEffect(() => {
    if (onEditComponent >= 0) onEditClick(pagebuilder.currentcomponents[onEditComponent], onEditComponent);
  }, [onEditComponent]);

  useEffect(() => {
    if (onMoveComponent !== null) moveComponent(onMoveFinished)(onMoveComponent);
  }, [onMoveComponent]);

  useEffect(() => {
    if (onMoveOutComponent !== null) moveOutComponent(onMoveOutFinished)(onMoveOutComponent);
  }, [onMoveOutComponent]);

  useEffect(() => {
    if (onCopyComponent !== null) copyComponent(onCopyFinished)(onCopyComponent);
  }, [onCopyComponent]);

  useEffect(() => {
    if (onDeleteComponent >= 0) deleteComponent(onCloseModals)(pagebuilder.currentcomponents[onDeleteComponent], onDeleteComponent);
  }, [onDeleteComponent]);

  useEffect(() => {
    if (PBSelectedComponentIndex >= 0) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
  }, [PBSelectedComponentIndex]);

  useEffect(() => {
    if (isPBAddModal || isPBEditSidebar) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
  }, [isPBAddModal, isPBEditSidebar]);

  useEffect(() => {
    if (PBComponentToEdit) renderSettings();
  }, [PBComponentToEdit]);

  const renderSettings = () => {
    let SettingsElement: JSX.Element = <></>;
    switch (PBComponentToEdit?.componentType) {
      case 'html':
        SettingsElement = <HTMLSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'stage':
        SettingsElement = <StageSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'ctabutton':
        SettingsElement = (
          <CTAButtonSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'boothlogolist':
        SettingsElement = (
          <BoothLogoListSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'booths':
        SettingsElement = <BoothsSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'presentationpreviewlist':
        SettingsElement = (
          <PresentationPreviewListSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'margin':
        SettingsElement = <MarginSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'heromenu':
        SettingsElement = (
          <HeroMenuSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'network':
        SettingsElement = (
          <NetworkSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'presentation':
        SettingsElement = (
          <PresentationSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'appointment':
        SettingsElement = (
          <AppointmentSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'media':
        SettingsElement = <MediaSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'eventhome':
        SettingsElement = (
          <EventHomeSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'infobooth':
        SettingsElement = (
          <InfoBoothSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'queue':
        SettingsElement = <QueueSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;

      // deprecated and moved to image component
      case 'gallery':
        SettingsElement = (
          <GallerySettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'signupform':
        SettingsElement = (
          <SignupFormSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'countdown':
        SettingsElement = (
          <CountdownSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'photobooth':
        SettingsElement = (
          <PhotoBoothSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'agenda':
        SettingsElement = <AgendSettings component={PBComponentToEdit} onChange={(c) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'challenge':
        SettingsElement = (
          <ChallengeSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'video':
        SettingsElement = <VideoSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'socialmedia':
        SettingsElement = (
          <SocialMediaSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'userprofile':
        SettingsElement = (
          <UserProfileSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'speakers':
        SettingsElement = (
          <SpeakersSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'image':
        SettingsElement = <ImageSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'speeddating':
        SettingsElement = (
          <SpeedDatingSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'allMatches':
        SettingsElement = (
          <AllMatchesSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'ticketcode':
        SettingsElement = (
          <TicketcodeSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'qa':
        SettingsElement = <QASettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'poll':
        SettingsElement = <PollSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;
      case 'eventslist':
        SettingsElement = (
          <EventsListSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;
      case 'teamheromenu':
        SettingsElement = (
          <TeamHeroMenuSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;

      case 'column':
        SettingsElement = <ColumnSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />;
        break;

      case 'divider':
        SettingsElement = (
          <DividerSettings component={PBComponentToEdit} onChange={(c: UiComponent) => editComponent(c)} onClose={closeModals(onCloseModals)} />
        );
        break;

      default:
        break;
    }

    setSettingsElement(SettingsElement);
  };

  return (
    <>
      <main
        className={`${isPBAddModal || isPBEditSidebar ? 'lock-screen' : ''} ${isMobilePreview ? 'max-h-screen overflow-auto' : ''} 
        flex text-white w-full min-h-full
      `}>
        {children}

        <VyModal
          onClose={closeModals(onCloseModals)}
          open={isPBAddModal}
          style={{ width: '80vw' }}
          maxWidth={'1333px'}
          backgroundColor="transparent"
          PaperProps={{
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}>
          {(onClose) => (
            <div>
              <AddModal
                closeModals={closeModals(onCloseModals)}
                onCreateComponent={onCreateComponent(onCloseModals)}
                isAddModal={isPBAddModal}
                isLandingPage={isLandingPage}
                isNetworkActive={event?.modules.networking.active}
                isChallengeActive={event?.modules.challenges.active}
                isAppointmentActive={event?.modules.booking.active}
                isVideoActive={event?.modules.preuploadedmedia.active || !!pageData?.team}
                supportedThemes={event?.organization.supportedPagebuilderThemes}
              />
            </div>
          )}
        </VyModal>

        <VyDrawer open={isPBEditSidebar && PBSelectedComponentIndex >= 0} onClose={() => closeModals(onCloseModals)()}>
          <div className="w-[500px] max-w-[90cqw]">{settingsElement && settingsElement}</div>
        </VyDrawer>
        {PBSpeakersModalComponent && <PBSpeakersModal onClose={() => dispatch(setPBSpeakersModalComponent(undefined))} />}
        {isPBBoothsModal && (
          <VyModal open={true} maxWidth="100wv" onClose={() => dispatch(setIsPBBoothsModal(false))} hasDefaultPadding={false}>
            {() => <PBBooths />}
          </VyModal>
        )}
        {isPBPresentationsModal && <PBPresentationsModal onClose={() => dispatch(setIsPBPresentationsModal(false))} />}
        {isPBMatchingModal && <PBMatchingModal onClose={() => dispatch(setIsPBMatchingModal(false))} />}
        {showBoothOnboardModal && (
          <BoothOnboardModal
            title={t('pagebuilder:create-new-booth')}
            onClose={() => dispatch(setShowBoothOnboardModal(false))}
            open={true}
            modalType={'add'}
            eventid={event?._id}
            refresh={() => event && dispatch(fetchAndSetBooths(event?.slug))}
          />
        )}

        {!!PBAddSpeakerComponent && <AddSpeakerModal />}
      </main>
    </>
  );
};

export default withTranslation('pagebuilder')(PageBuilder);
